import type { ApplicationJourneyHeaderDto, LoanReviewSummaryDto } from '../api/admin';

export type ManualReviewHoldReason =
  | 'EMPLOYMENT_PENDING'
  | 'AGENT_REVIEW'
  | 'EMPLOYEE_CONFIRMATION';

export type ManualReviewHold = NonNullable<LoanReviewSummaryDto['manualReviewHold']>;

export function readManualReviewHold(
  loanReview: ApplicationJourneyHeaderDto['loanReview'] | undefined,
): ManualReviewHold | null {
  return loanReview?.manualReviewHold ?? null;
}

/** True when MANUAL_REVIEW is held for employment clearance, not agent REVIEW items. */
export function isEmploymentPendingManualReviewHold(input: {
  status?: string;
  loanReview?: ApplicationJourneyHeaderDto['loanReview'];
  completedMilestones?: string[];
}): boolean {
  if (input.status?.trim().toUpperCase() !== 'MANUAL_REVIEW') {
    return false;
  }
  const hold = readManualReviewHold(input.loanReview);
  if (hold?.reason === 'EMPLOYMENT_PENDING') {
    return true;
  }
  // Other hold reasons (AGENT_REVIEW, EMPLOYEE_CONFIRMATION) are not employment clearance.
  if (hold) {
    return false;
  }
  // Pre-metadata fallback: APPROVE parked while employment pending review milestone exists.
  if (input.loanReview?.decision === 'APPROVE') {
    const milestones = new Set((input.completedMilestones ?? []).map((m) => m.toUpperCase()));
    return milestones.has('EMPLOYMENT_PENDING_REVIEW');
  }
  return false;
}

export function manualReviewHoldBannerCopy(input: {
  status?: string;
  loanReview?: ApplicationJourneyHeaderDto['loanReview'];
  completedMilestones?: string[];
}): { title: string; body: string; navigateTab: 'employer' | 'loan-review' } | null {
  if (input.status?.trim().toUpperCase() !== 'MANUAL_REVIEW') {
    return null;
  }
  const hold = readManualReviewHold(input.loanReview);
  if (isEmploymentPendingManualReviewHold(input)) {
    return {
      title: 'Awaiting employment clearance',
      body:
        hold?.detail?.trim() ||
        'Loan review approved this application. Funding stays on hold until employment verification clears. Accept only after employer checks are complete.',
      navigateTab: 'employer',
    };
  }
  if (hold?.reason === 'EMPLOYEE_CONFIRMATION' && input.loanReview?.decision === 'APPROVE') {
    return {
      title: 'Confirm agent approval',
      body:
        hold.detail?.trim() ||
        'The loan-review agent recommended approval. Accept to proceed to funding or reject to decline.',
      navigateTab: 'loan-review',
    };
  }
  if (hold?.reason === 'AGENT_REVIEW') {
    return {
      title: 'Agent flagged for review',
      body:
        hold.detail?.trim() ||
        'The loan-review agent found items that need a human decision before funding.',
      navigateTab: 'loan-review',
    };
  }
  return null;
}
